import {
  IonContentCustomEvent,
  menuController,
  RefresherEventDetail,
  ScrollDetail,
} from '@ionic/core/components'
import { IonButton, IonHeader, IonPage, IonRefresher } from '@ionic/react'

import { debounce } from 'lodash'
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Helmet } from 'react-helmet-async'
import { NumberParam, useQueryParam, withDefault } from 'use-query-params'
import { MenuIcon } from '~/shared/ui/icons'
import { myPalette } from '~/shared/ui/theme'
import { ButtonBack } from '../ButtonBack'
import { ScrollToTopButton } from './ScrollToTopButton'
import {
  TopContent,
  Content,
  WrapperHeader,
  WrapperCap,
  IonToolbar,
  IonContent,
  Cap,
  ContentWrapper,
  IonRefresherContent,
} from './styled'

const SCROLL_THRESHOLD = 400

type Props = {
  children: ReactNode
  title: string
  canMenu?: boolean
  canBack?: boolean
  onBack?: () => void
  backTitle?: ReactNode | string
  topContent?: ReactNode
  extraHeader?: ReactNode
  preventScrollReset?: boolean
  contentFillColor?: string | null
  onRefreshPage?: () => Promise<void>
}

export const ScrollContext = createContext<{ scrollToTop: () => void } | null>(
  null,
)

export function PageContainer({
  title,
  canMenu,
  children,
  canBack,
  onBack,
  backTitle,
  topContent,
  extraHeader,
  preventScrollReset,
  contentFillColor = myPalette.aliceBlue.main,
  onRefreshPage,
}: Props) {
  const [isVisibleToTop, setIsVisibleToTop] = useState(false)
  const openFirstMenu = useCallback(async () => {
    await menuController.open('main-menu')
  }, [])

  const contentRef = useRef<HTMLIonContentElement>(null)

  const [positionTopQuery, setPositionTop] = useQueryParam(
    'pagePositionTop',
    withDefault(NumberParam, 0),
  )

  const handleScrollChangeQuery = useCallback(
    (scrollTop?: number) => {
      setPositionTop(scrollTop || 0, 'replaceIn')
    },
    [setPositionTop],
  )

  const handleScroll = useMemo(
    () =>
      debounce((event: IonContentCustomEvent<ScrollDetail>) => {
        const scrollTop = event.detail.scrollTop
        preventScrollReset && handleScrollChangeQuery(scrollTop)
        setIsVisibleToTop(scrollTop > SCROLL_THRESHOLD)
      }, 100),
    [handleScrollChangeQuery, preventScrollReset],
  )

  const handleScrollToPoint = useCallback(() => {
    contentRef?.current?.scrollToPoint?.(0, positionTopQuery, 0)
  }, [contentRef, positionTopQuery])

  const scrollToTop = useCallback(() => {
    contentRef.current?.scrollToTop?.(0)
  }, [contentRef])

  useEffect(() => {
    if (positionTopQuery) handleScrollToPoint()
  }, [positionTopQuery, handleScrollToPoint])

  useEffect(() => {
    return () => {
      handleScroll.cancel()
    }
  }, [handleScroll])

  const scrollContextValue = useMemo(() => ({ scrollToTop }), [scrollToTop])

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    try {
      await onRefreshPage?.()
    } finally {
      event.detail.complete()
    }
  }

  return (
    <ScrollContext.Provider value={scrollContextValue}>
      <IonPage
        id='page-container'
        style={{
          background: myPalette.midnightBlue.main,
        }}
      >
        <Helmet>
          <title>{title}</title>
        </Helmet>
        <IonHeader>
          <IonToolbar>
            <WrapperHeader>
              {canMenu && (
                <IonButton
                  fill='clear'
                  onClick={openFirstMenu}
                  data-testid='home-menu-button'
                >
                  <MenuIcon />
                </IonButton>
              )}

              {canBack && (
                <ButtonBack
                  title={backTitle}
                  onBack={onBack}
                  style={{ marginRight: 1 }}
                />
              )}
              {extraHeader}
            </WrapperHeader>
          </IonToolbar>
        </IonHeader>

        <IonContent
          fullscreen
          ref={contentRef}
          scrollEvents
          onIonScroll={handleScroll}
        >
          {Boolean(onRefreshPage) && (
            <IonRefresher slot='fixed' mode='md' onIonRefresh={handleRefresh}>
              <IonRefresherContent
                pullingIcon='bubbles'
                refreshingSpinner='bubbles'
              />
            </IonRefresher>
          )}
          <ContentWrapper>
            {topContent ? (
              <TopContent>{topContent}</TopContent>
            ) : (
              <div style={{ padding: '9px' }} />
            )}

            <WrapperCap>
              <Cap $contentFillColor={contentFillColor} />
            </WrapperCap>
            <Content $contentFillColor={contentFillColor}>{children}</Content>
          </ContentWrapper>
        </IonContent>

        <ScrollToTopButton contentRef={contentRef} isVisible={isVisibleToTop} />
      </IonPage>
    </ScrollContext.Provider>
  )
}
